import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext();

export function DataProvider({ children }) {
    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [faculty, setFaculty] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [timetableEntries, setTimetableEntries] = useState({}); // Key: "day-slotId"
    const [departments, setDepartments] = useState([]);
    const [timeConfigs, setTimeConfigs] = useState([]);
    const [loading, setLoading] = useState(true);

    const API_URL = '/api';

    const fetchData = async () => {
        try {
            setLoading(true);
            const [subjRes, sectRes, facRes, mapRes, deptRes, timeRes] = await Promise.all([
                fetch(`${API_URL}/subjects`),
                fetch(`${API_URL}/sections`),
                fetch(`${API_URL}/faculty`),
                fetch(`${API_URL}/mappings`),
                fetch(`${API_URL}/departments`),
                fetch(`${API_URL}/time-configs`)
            ]);

            setSubjects(await subjRes.json());
            setSections(await sectRes.json());
            setFaculty(await facRes.json());
            setMappings(await mapRes.json());
            setDepartments(await deptRes.json());
            setTimeConfigs(await timeRes.json());
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Actions ---

    const addSubject = async (subject) => {
        try {
            const res = await fetch(`${API_URL}/subjects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(subject)
            });
            if (res.ok) {
                const newSubject = await res.json();
                setSubjects(prev => [...prev, newSubject]);
            }
        } catch (error) {
            console.error("Error adding subject:", error);
        }
    };

    const addSection = async (section) => {
        try {
            const res = await fetch(`${API_URL}/sections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(section)
            });
            if (res.ok) {
                const newSection = await res.json();
                setSections(prev => [...prev, newSection]);
            }
        } catch (error) {
            console.error("Error adding section:", error);
        }
    };

    const updateSection = async (id, updates) => {
        try {
            const res = await fetch(`${API_URL}/sections/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            if (res.ok) {
                const updated = await res.json();
                setSections(prev => prev.map(s => s.id === id ? updated : s));
            }
        } catch (error) {
            console.error("Error updating section:", error);
        }
    };

    const deleteSection = async (id) => {
        try {
            const res = await fetch(`${API_URL}/sections/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setSections(prev => prev.filter(s => s.id !== id));
            }
        } catch (error) {
            console.error("Error deleting section:", error);
        }
    };

    // Placeholder for addFaculty (similar pattern)
    const addFaculty = async (newFac) => {
        try {
            const res = await fetch(`${API_URL}/faculty`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newFac)
            });
            if (res.ok) {
                const created = await res.json();
                setFaculty(prev => [...prev, created]);
            }
        } catch (error) {
            console.error("Error adding faculty:", error);
        }
    };

    const addMapping = async (mapping) => {
        try {
            const res = await fetch(`${API_URL}/mappings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(mapping)
            });
            if (res.ok) {
                const newMapping = await res.json();
                setMappings(prev => [...prev, newMapping]);
            }
        } catch (error) {
            console.error("Error adding mapping:", error);
        }
    };

    const deleteMapping = async (id) => {
        try {
            const res = await fetch(`${API_URL}/mappings/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMappings(prev => prev.filter(m => m.id !== id));
            }
        } catch (error) {
            console.error("Error deleting mapping:", error);
        }
    };

    // Fetch timetable for a specific section
    const fetchTimetableForSection = async (sectionId) => {
        try {
            const res = await fetch(`${API_URL}/timetable?sectionId=${sectionId}`);
            if (res.ok) {
                const data = await res.json();
                setTimetableEntries(data); // Replace current view with filtered data
            }
        } catch (error) {
            console.error("Error fetching timetable:", error);
        }
    };

    const updateTimetableEntry = async (day, slotId, sectionId, mappingId, faculty2Id = null) => {
        try {
            const res = await fetch(`${API_URL}/timetable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ day, slotId, sectionId, mappingId, faculty2Id })
            });

            if (res.ok) {
                setTimetableEntries(prev => {
                    const key = `${day}-${slotId}`;
                    const newSlots = { ...prev };
                    if (mappingId === null) {
                        delete newSlots[key];
                    } else {
                        // Store full object locally to match backend response format
                        newSlots[key] = { mappingId, faculty2Id };
                    }
                    return newSlots;
                });
            }
        } catch (error) {
            console.error("Error updating timetable:", error);
        }
    };


    // --- Attendance & Students ---
    const fetchStudents = async (sectionId) => {
        try {
            const res = await fetch(`${API_URL}/sections/${sectionId}/students`);
            return await res.json();
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const fetchAttendance = async (mappingId, date) => {
        try {
            let url = `${API_URL}/attendance?mappingId=${mappingId}`;
            if (date) url += `&date=${date}`;
            const res = await fetch(url);
            return await res.json();
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const markAttendance = async (date, mappingId, records) => {
        try {
            const res = await fetch(`${API_URL}/attendance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ date, mappingId, records })
            });
            return res.ok;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const fetchMyMappings = async (userId) => {
        try {
            const res = await fetch(`${API_URL}/my-mappings?userId=${userId}`);
            if (res.ok) return await res.json();
            return [];
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    return (
        <DataContext.Provider value={{
            subjects, sections, faculty, mappings, timetableEntries, departments, timeConfigs, loading,
            addSubject, addSection, updateSection, deleteSection, addFaculty, addMapping, deleteMapping, updateTimetableEntry, fetchTimetableForSection,
            fetchStudents, fetchAttendance, markAttendance, fetchMyMappings,
            refreshData: fetchData
        }}>
            {children}
        </DataContext.Provider>
    );
}

export function useData() {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
}
